import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { FolderItem } from "global/components/Folders/types";
import { usePopover } from "hooks/use-popover";
import { convertESTtoUserLocalDateAndTime } from "helpers/methods";
import signRequestsStyles, {
  TRAILING_ACTION_CLASS,
  TRAILING_CHEVRON_CLASS,
} from "../SignRequests.styles";
import signRequestsTokens from "../SignRequests.tokens";
import {
  ChevronRightGlyph,
  FolderGlyph,
  KebabGlyph,
} from "./SignRequestsIcons";

const styles = signRequestsStyles;
const { row } = signRequestsTokens;

interface SentFolderRowProps {
  item: FolderItem;
  isLoading: boolean;
  menuItems: string[];
  onOpen: (item: FolderItem) => void;
  onMenuItem: (item: FolderItem, label: string) => void;
}

/**
 * One folder row inside the Sign Requests list. Sits on the request rows'
 * column grid — its name spans every column but the last — so both row types
 * line up down the card.
 *
 * The design's last column holds a chevron. Folders also need their rename /
 * move / delete menu, so the chevron swaps for the overflow trigger while the
 * row is hovered or its menu is open — the resting row stays exactly as drawn.
 */
const SentFolderRow = ({
  item,
  isLoading,
  menuItems,
  onOpen,
  onMenuItem,
}: SentFolderRowProps) => {
  const popover = usePopover<HTMLElement>();
  const hasMenu = menuItems.length > 0;

  const createdOnRaw = item.createdOn ?? item.createdAt ?? "";
  const createdOn = createdOnRaw
    ? convertESTtoUserLocalDateAndTime(
        createdOnRaw,
        "MMM D, YYYY, h:mm A",
        "MM/DD/YYYY HH:mm:ss",
      )
    : "";

  /*
    A folder read from the folder tree carries only its name, so its count is
    left out rather than shown as "0 requests".
  */
  const subParts = [
    ...(typeof item.itemCount === "number"
      ? [`${item.itemCount} ${item.itemCount === 1 ? "request" : "requests"}`]
      : []),
    ...(createdOn ? [`Created ${createdOn}`] : []),
  ];

  return (
    <Box
      sx={styles.folderRow}
      onClick={() => {
        if (!isLoading) onOpen(item);
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label={`Open folder ${item.templateName}`}
        disabled={isLoading}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          if (!isLoading) onOpen(item);
        }}
        sx={styles.rowOpenButton}
      />
      <Box sx={styles.folderLead}>
        <Box sx={styles.folderIconTile}>
          <FolderGlyph sx={styles.folderGlyph} />
        </Box>
        <Box sx={styles.rowText}>
          <Typography component="div" sx={styles.folderTitle}>
            {item.templateName}
          </Typography>
          {subParts.length > 0 && (
            <Typography component="div" sx={styles.folderSub}>
              {subParts.join(" · ")}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={styles.trailingColumn}>
        <Box
          className={TRAILING_CHEVRON_CLASS}
          sx={styles.trailingChevron(hasMenu && popover.open)}
        >
          <ChevronRightGlyph
            sx={{ width: row.chevronSize, height: row.chevronSize }}
          />
        </Box>
        {hasMenu && (
          <Box
            className={TRAILING_ACTION_CLASS}
            sx={styles.trailingAction(popover.open)}
          >
            <IconButton
              sx={styles.kebabButton}
              disabled={isLoading}
              aria-label={`Options for folder ${item.templateName}`}
              onClick={(event) => {
                event.stopPropagation();
                popover.anchorRef.current = event.currentTarget;
                popover.handleOpen();
              }}
              ref={
                popover.anchorRef as React.MutableRefObject<HTMLButtonElement | null>
              }
            >
              <KebabGlyph sx={styles.kebabGlyph} />
            </IconButton>
            <Menu
              anchorEl={popover.anchorRef.current}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              open={popover.open}
              onClose={popover.handleClose}
              onClick={(event) => event.stopPropagation()}
            >
              {menuItems.map((label) => (
                <MenuItem
                  key={label}
                  onClick={() => {
                    popover.handleClose();
                    onMenuItem(item, label);
                  }}
                >
                  {label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SentFolderRow;
